import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, BookOpen, EnvelopeSimple, SignOut, Plus, Pencil, Trash, 
  House, Eye, EyeSlash, X
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose 
} from '../components/ui/dialog';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { id: 'products', label: 'Produk', icon: Package },
  { id: 'case-studies', label: 'Case Study', icon: BookOpen },
  { id: 'messages', label: 'Pesan', icon: EnvelopeSimple },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { token, logout, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [productDialog, setProductDialog] = useState(false);
  const [caseStudyDialog, setCaseStudyDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCaseStudy, setEditingCaseStudy] = useState(null);
  
  // Form states
  const [productForm, setProductForm] = useState({
    name: '', description: '', image_url: '', price: '', price_type: 'fixed', specs: '', category: 'hardware', product_type: 'featured', brochure_url: '', is_active: true
  });
  const [caseStudyForm, setCaseStudyForm] = useState({
    title: '', description: '', image_url: '', industry: '', content: '', is_active: true
  });

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, caseStudiesRes, messagesRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/case-studies`),
        axios.get(`${API}/contact`, authHeaders)
      ]);
      setProducts(productsRes.data);
      setCaseStudies(caseStudiesRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/admin');
      }
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  // Product CRUD
  const openProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description,
        image_url: product.image_url,
        price: product.price.toString(),
        price_type: product.price_type || 'fixed',
        specs: product.specs || '',
        category: product.category,
        product_type: product.product_type || 'featured',
        brochure_url: product.brochure_url || '',
        is_active: product.is_active
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '', description: '', image_url: '', price: '', price_type: 'fixed', specs: '', category: 'hardware', product_type: 'featured', brochure_url: '', is_active: true
      });
    }
    setProductDialog(true);
  };

  const saveProduct = async () => {
    try {
      const data = { ...productForm, price: productForm.price_type === 'call' ? 0 : parseFloat(productForm.price) };
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data, authHeaders);
        toast.success('Produk berhasil diperbarui');
      } else {
        await axios.post(`${API}/products`, data, authHeaders);
        toast.success('Produk berhasil ditambahkan');
      }
      setProductDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Gagal menyimpan produk');
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Hapus produk ini?')) return;
    try {
      await axios.delete(`${API}/products/${id}`, authHeaders);
      toast.success('Produk berhasil dihapus');
      fetchData();
    } catch (error) {
      toast.error('Gagal menghapus produk');
    }
  };

  // Case Study CRUD
  const openCaseStudyDialog = (study = null) => {
    if (study) {
      setEditingCaseStudy(study);
      setCaseStudyForm({
        title: study.title,
        description: study.description,
        image_url: study.image_url,
        industry: study.industry,
        content: study.content,
        is_active: study.is_active
      });
    } else {
      setEditingCaseStudy(null);
      setCaseStudyForm({
        title: '', description: '', image_url: '', industry: '', content: '', is_active: true
      });
    }
    setCaseStudyDialog(true);
  };

  const saveCaseStudy = async () => {
    try {
      if (editingCaseStudy) {
        await axios.put(`${API}/case-studies/${editingCaseStudy.id}`, caseStudyForm, authHeaders);
        toast.success('Case study berhasil diperbarui');
      } else {
        await axios.post(`${API}/case-studies`, caseStudyForm, authHeaders);
        toast.success('Case study berhasil ditambahkan');
      }
      setCaseStudyDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Gagal menyimpan case study');
    }
  };

  const deleteCaseStudy = async (id) => {
    if (!window.confirm('Hapus case study ini?')) return;
    try {
      await axios.delete(`${API}/case-studies/${id}`, authHeaders);
      toast.success('Case study berhasil dihapus');
      fetchData();
    } catch (error) {
      toast.error('Gagal menghapus case study');
    }
  };

  const toggleProductActive = async (product) => {
    try {
      await axios.put(`${API}/products/${product.id}`, { is_active: !product.is_active }, authHeaders);
      toast.success(product.is_active ? 'Produk dinonaktifkan' : 'Produk diaktifkan');
      fetchData();
    } catch (error) {
      toast.error('Gagal mengubah status');
    }
  };

  const toggleCaseStudyActive = async (study) => {
    try {
      await axios.put(`${API}/case-studies/${study.id}`, { is_active: !study.is_active }, authHeaders);
      toast.success(study.is_active ? 'Case study dinonaktifkan' : 'Case study diaktifkan');
      fetchData();
    } catch (error) {
      toast.error('Gagal mengubah status');
    }
  };

  return (
    <div className="min-h-screen bg-xgrids-bg-primary" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-screen w-64 bg-xgrids-bg-secondary border-r border-white/10 p-6 flex flex-col">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <img src="https://xgrids.com/assets/logo_white.ceffc551.png" alt="XGRIDS" className="h-7" />
          <span className="font-heading font-bold text-xgrids-brand-primary">.ID</span>
        </Link>

        <nav className="flex-1 space-y-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors rounded-sm ${
                activeTab === tab.id 
                  ? 'bg-xgrids-brand-primary/10 text-xgrids-brand-primary border border-xgrids-brand-primary/30' 
                  : 'text-xgrids-text-secondary hover:text-white hover:bg-white/5'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={20} />
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="space-y-2 pt-6 border-t border-white/10">
          <Link
            to="/"
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-xgrids-text-secondary hover:text-white hover:bg-white/5 rounded-sm"
            data-testid="view-site-btn"
          >
            <House size={20} />
            Lihat Website
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm"
            data-testid="logout-btn"
          >
            <SignOut size={20} />
            Keluar
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Products Tab */}
          {activeTab === 'products' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-heading text-2xl font-bold text-white" data-testid="products-tab-title">Kelola Produk</h1>
                  <p className="text-xgrids-text-secondary text-sm mt-1">{products.length} produk terdaftar</p>
                </div>
                <Button 
                  onClick={() => openProductDialog()} 
                  className="bg-xgrids-brand-primary text-xgrids-bg-primary hover:bg-xgrids-brand-hover rounded-none"
                  data-testid="add-product-btn"
                >
                  <Plus size={18} className="mr-2" />
                  Tambah Produk
                </Button>
              </div>

              <div className="bg-xgrids-bg-surface border border-white/10 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-xgrids-text-secondary">Gambar</TableHead>
                      <TableHead className="text-xgrids-text-secondary">Nama</TableHead>
                      <TableHead className="text-xgrids-text-secondary">Tipe</TableHead>
                      <TableHead className="text-xgrids-text-secondary">Harga</TableHead>
                      <TableHead className="text-xgrids-text-secondary">Status</TableHead>
                      <TableHead className="text-xgrids-text-secondary text-right">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-xgrids-text-secondary">
                          Memuat...
                        </TableCell>
                      </TableRow>
                    ) : products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-xgrids-text-secondary">
                          Belum ada produk
                        </TableCell>
                      </TableRow>
                    ) : (
                      products.map((product, idx) => (
                        <TableRow key={product.id} className="border-white/10" data-testid={`product-row-${idx}`}>
                          <TableCell>
                            <img src={product.image_url} alt={product.name} className="w-16 h-16 object-contain bg-black/30 rounded" />
                          </TableCell>
                          <TableCell className="text-white font-medium">{product.name}</TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-1 rounded ${
                              product.product_type === 'featured' ? 'bg-yellow-500/20 text-yellow-400' :
                              product.product_type === 'xgrids' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-purple-500/20 text-purple-400'
                            }`}>
                              {product.product_type === 'featured' ? 'Unggulan' :
                               product.product_type === 'xgrids' ? 'XGRIDS' : 'Supporting'}
                            </span>
                          </TableCell>
                          <TableCell className="text-xgrids-brand-primary font-mono">
                            {product.price_type === 'call' ? 'Call' : `Rp ${product.price.toLocaleString('id-ID')}`}
                          </TableCell>
                          <TableCell>
                            <button 
                              onClick={() => toggleProductActive(product)}
                              className={`flex items-center gap-1.5 text-sm ${product.is_active ? 'text-green-400' : 'text-xgrids-text-muted'}`}
                              data-testid={`toggle-product-${idx}`}
                            >
                              {product.is_active ? <Eye size={16} /> : <EyeSlash size={16} />}
                              {product.is_active ? 'Aktif' : 'Nonaktif'}
                            </button>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openProductDialog(product)}
                                className="text-xgrids-text-secondary hover:text-white"
                                data-testid={`edit-product-${idx}`}
                              >
                                <Pencil size={16} />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => deleteProduct(product.id)}
                                className="text-red-400 hover:text-red-300"
                                data-testid={`delete-product-${idx}`}
                              >
                                <Trash size={16} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Case Studies Tab */}
          {activeTab === 'case-studies' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-heading text-2xl font-bold text-white" data-testid="case-studies-tab-title">Kelola Case Study</h1>
                  <p className="text-xgrids-text-secondary text-sm mt-1">{caseStudies.length} case study terdaftar</p>
                </div>
                <Button 
                  onClick={() => openCaseStudyDialog()} 
                  className="bg-xgrids-brand-primary text-xgrids-bg-primary hover:bg-xgrids-brand-hover rounded-none"
                  data-testid="add-case-study-btn"
                >
                  <Plus size={18} className="mr-2" />
                  Tambah Case Study
                </Button>
              </div>

              <div className="bg-xgrids-bg-surface border border-white/10 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-xgrids-text-secondary">Gambar</TableHead>
                      <TableHead className="text-xgrids-text-secondary">Judul</TableHead>
                      <TableHead className="text-xgrids-text-secondary">Industri</TableHead>
                      <TableHead className="text-xgrids-text-secondary">Status</TableHead>
                      <TableHead className="text-xgrids-text-secondary text-right">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-xgrids-text-secondary">
                          Memuat...
                        </TableCell>
                      </TableRow>
                    ) : caseStudies.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-xgrids-text-secondary">
                          Belum ada case study
                        </TableCell>
                      </TableRow>
                    ) : (
                      caseStudies.map((study, idx) => (
                        <TableRow key={study.id} className="border-white/10" data-testid={`case-study-row-${idx}`}>
                          <TableCell>
                            <img src={study.image_url} alt={study.title} className="w-20 h-12 object-cover rounded" />
                          </TableCell>
                          <TableCell className="text-white font-medium max-w-xs truncate">{study.title}</TableCell>
                          <TableCell className="text-xgrids-brand-blue">{study.industry}</TableCell>
                          <TableCell>
                            <button 
                              onClick={() => toggleCaseStudyActive(study)}
                              className={`flex items-center gap-1.5 text-sm ${study.is_active ? 'text-green-400' : 'text-xgrids-text-muted'}`}
                              data-testid={`toggle-case-study-${idx}`}
                            >
                              {study.is_active ? <Eye size={16} /> : <EyeSlash size={16} />}
                              {study.is_active ? 'Aktif' : 'Nonaktif'}
                            </button>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openCaseStudyDialog(study)}
                                className="text-xgrids-text-secondary hover:text-white"
                                data-testid={`edit-case-study-${idx}`}
                              >
                                <Pencil size={16} />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => deleteCaseStudy(study.id)}
                                className="text-red-400 hover:text-red-300"
                                data-testid={`delete-case-study-${idx}`}
                              >
                                <Trash size={16} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div>
              <div className="mb-8">
                <h1 className="font-heading text-2xl font-bold text-white" data-testid="messages-tab-title">Pesan Masuk</h1>
                <p className="text-xgrids-text-secondary text-sm mt-1">{messages.length} pesan</p>
              </div>

              <div className="space-y-4">
                {loading ? (
                  <div className="text-center py-8 text-xgrids-text-secondary">Memuat...</div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8 text-xgrids-text-secondary bg-xgrids-bg-surface border border-white/10 rounded-sm">
                    Belum ada pesan masuk
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div 
                      key={msg.id} 
                      className="bg-xgrids-bg-surface border border-white/10 p-6 rounded-sm"
                      data-testid={`message-${idx}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-heading font-bold text-white">{msg.name}</h3>
                          <p className="text-xgrids-text-secondary text-sm">{msg.email}</p>
                          {msg.company && <p className="text-xgrids-text-muted text-sm">{msg.company}</p>}
                        </div>
                        <span className="text-xgrids-text-muted text-xs">
                          {new Date(msg.created_at).toLocaleDateString('id-ID', { 
                            day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p className="text-xgrids-text-secondary whitespace-pre-wrap">{msg.message}</p>
                      {msg.phone && (
                        <p className="text-xgrids-brand-primary text-sm mt-4">Tel: {msg.phone}</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </motion.div>
      </main>

      {/* Product Dialog */}
      <Dialog open={productDialog} onOpenChange={setProductDialog}>
        <DialogContent className="bg-xgrids-bg-surface border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingProduct ? 'Edit Produk' : 'Tambah Produk Baru'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm text-xgrids-text-secondary mb-2">Nama Produk *</label>
              <Input
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                className="bg-xgrids-bg-primary border-white/10 text-white rounded-none"
                data-testid="product-name-input"
              />
            </div>
            <div>
              <label className="block text-sm text-xgrids-text-secondary mb-2">URL Gambar *</label>
              <Input
                value={productForm.image_url}
                onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                placeholder="https://..."
                className="bg-xgrids-bg-primary border-white/10 text-white rounded-none"
                data-testid="product-image-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-xgrids-text-secondary mb-2">Tipe Harga</label>
                <select
                  value={productForm.price_type}
                  onChange={(e) => setProductForm({ ...productForm, price_type: e.target.value })}
                  className="w-full bg-xgrids-bg-primary border border-white/10 text-white rounded-none px-3 py-2"
                  data-testid="product-price-type-select"
                >
                  <option value="fixed">Harga Tetap</option>
                  <option value="call">Call (Hubungi Kami)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-xgrids-text-secondary mb-2">Harga (Rp)</label>
                <Input
                  type="number"
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                  disabled={productForm.price_type === 'call'}
                  placeholder={productForm.price_type === 'call' ? 'N/A' : '0'}
                  className="bg-xgrids-bg-primary border-white/10 text-white rounded-none disabled:opacity-50"
                  data-testid="product-price-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-xgrids-text-secondary mb-2">Kategori</label>
                <select
                  value={productForm.category}
                  onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                  className="w-full bg-xgrids-bg-primary border border-white/10 text-white rounded-none px-3 py-2"
                  data-testid="product-category-select"
                >
                  <option value="hardware">Hardware</option>
                  <option value="software">Software</option>
                  <option value="plugin">Plugin</option>
                  <option value="gnss">GNSS</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-xgrids-text-secondary mb-2">Tipe Produk</label>
                <select
                  value={productForm.product_type}
                  onChange={(e) => setProductForm({ ...productForm, product_type: e.target.value })}
                  className="w-full bg-xgrids-bg-primary border border-white/10 text-white rounded-none px-3 py-2"
                  data-testid="product-type-select"
                >
                  <option value="featured">Produk Unggulan</option>
                  <option value="xgrids">Semua Produk XGRIDS</option>
                  <option value="supporting">Supporting Product</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm text-xgrids-text-secondary mb-2">URL Katalog/Brochure</label>
              <Input
                value={productForm.brochure_url}
                onChange={(e) => setProductForm({ ...productForm, brochure_url: e.target.value })}
                placeholder="https://xgrids.com/product-brochure"
                className="bg-xgrids-bg-primary border-white/10 text-white rounded-none"
                data-testid="product-brochure-input"
              />
            </div>
            <div>
              <label className="block text-sm text-xgrids-text-secondary mb-2">Spesifikasi (pisahkan dengan |)</label>
              <Input
                value={productForm.specs}
                onChange={(e) => setProductForm({ ...productForm, specs: e.target.value })}
                placeholder="LiDAR | SLAM | Real-time"
                className="bg-xgrids-bg-primary border-white/10 text-white rounded-none"
                data-testid="product-specs-input"
              />
            </div>
            <div>
              <label className="block text-sm text-xgrids-text-secondary mb-2">Deskripsi *</label>
              <Textarea
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                rows={4}
                className="bg-xgrids-bg-primary border-white/10 text-white rounded-none resize-none"
                data-testid="product-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" className="text-xgrids-text-secondary">Batal</Button>
            </DialogClose>
            <Button 
              onClick={saveProduct}
              className="bg-xgrids-brand-primary text-xgrids-bg-primary hover:bg-xgrids-brand-hover rounded-none"
              data-testid="save-product-btn"
            >
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Case Study Dialog */}
      <Dialog open={caseStudyDialog} onOpenChange={setCaseStudyDialog}>
        <DialogContent className="bg-xgrids-bg-surface border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCaseStudy ? 'Edit Case Study' : 'Tambah Case Study Baru'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm text-xgrids-text-secondary mb-2">Judul *</label>
              <Input
                value={caseStudyForm.title}
                onChange={(e) => setCaseStudyForm({ ...caseStudyForm, title: e.target.value })}
                className="bg-xgrids-bg-primary border-white/10 text-white rounded-none"
                data-testid="case-study-title-input"
              />
            </div>
            <div>
              <label className="block text-sm text-xgrids-text-secondary mb-2">URL Gambar *</label>
              <Input
                value={caseStudyForm.image_url}
                onChange={(e) => setCaseStudyForm({ ...caseStudyForm, image_url: e.target.value })}
                placeholder="https://..."
                className="bg-xgrids-bg-primary border-white/10 text-white rounded-none"
                data-testid="case-study-image-input"
              />
            </div>
            <div>
              <label className="block text-sm text-xgrids-text-secondary mb-2">Industri *</label>
              <Input
                value={caseStudyForm.industry}
                onChange={(e) => setCaseStudyForm({ ...caseStudyForm, industry: e.target.value })}
                placeholder="Survei & Geospasial"
                className="bg-xgrids-bg-primary border-white/10 text-white rounded-none"
                data-testid="case-study-industry-input"
              />
            </div>
            <div>
              <label className="block text-sm text-xgrids-text-secondary mb-2">Deskripsi Singkat *</label>
              <Textarea
                value={caseStudyForm.description}
                onChange={(e) => setCaseStudyForm({ ...caseStudyForm, description: e.target.value })}
                rows={2}
                className="bg-xgrids-bg-primary border-white/10 text-white rounded-none resize-none"
                data-testid="case-study-desc-input"
              />
            </div>
            <div>
              <label className="block text-sm text-xgrids-text-secondary mb-2">Konten Lengkap *</label>
              <Textarea
                value={caseStudyForm.content}
                onChange={(e) => setCaseStudyForm({ ...caseStudyForm, content: e.target.value })}
                rows={6}
                className="bg-xgrids-bg-primary border-white/10 text-white rounded-none resize-none"
                data-testid="case-study-content-input"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" className="text-xgrids-text-secondary">Batal</Button>
            </DialogClose>
            <Button 
              onClick={saveCaseStudy}
              className="bg-xgrids-brand-primary text-xgrids-bg-primary hover:bg-xgrids-brand-hover rounded-none"
              data-testid="save-case-study-btn"
            >
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
